import React, { useEffect, useRef, useState } from 'react'

import { useLocation } from 'react-router-dom'
import mixpanel from 'mixpanel-browser'

import SupplierJourneyContext from 'contexts/SupplierJourneyContext'

import useFetch from 'hooks/useFetch'

import * as service from 'service'
import { getMixPanelAuthKey } from 'service/env'

const SupplierJourneyProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [supplier, setSupplier] = useState(null)
  const [company, setCompany] = useState(null)
  const [user, setUser] = useState(null)
  const [steps, setSteps] = useState([])
  const [activeStep, setActiveStep] = useState(0)
  const [stepInfo, setStepInfo] = useState()
  const [nextDisabled, setNextDisabled] = useState(false)
  const [recaptcha, setRecaptcha] = useState()
  const [international, setInternational] = useState(false)
  const [portalToken, setPortalToken] = useState(null)
  const [companySupplierToken, setCompanySupplierToken] = useState(null)
  const [companySupplierId, setCompanySupplierId] = useState(null)
  const [accessToken, setAccessToken] = useState(null)

  const { pathname } = useLocation()

  const {
    response,
    error,
    isLoading: isCompanyLoading,
    refresh,
  } = useFetch(
    service.supplierPortal.companySuppliers.showByToken,
    { token: portalToken },
    [portalToken],
    !!portalToken,
  )

  const loaded = !isCompanyLoading && !isLoading
  const linkInvalid = error?.response?.status === 404

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1)
      setStepInfo(steps[activeStep + 1])
    }
  }

  const recaptchaRef = useRef(null)

  const resetRecaptcha = () => {
    if (recaptchaRef.current) recaptchaRef.current.reset()
    setRecaptcha(undefined)
  }

  const handleAcceptedRecaptcha = (recaptchaToken) => {
    setRecaptcha(recaptchaToken)
  }

  const disableRecaptcha = () => setRecaptcha(undefined)

  const mixpanelBrowser = () => {
    mixpanel.init(getMixPanelAuthKey(), {
      debug: false, // TRUE somente em um ambiente de desenvolvimento
      ignore_dnt: true,
      secure_cookie: true,
      cross_site_cookie: false,
      api_transport: 'XHR',
      persistence: 'cookie',
      batch_requests: false,
    })

    if (!!company) {
      mixpanel.register_once({
        supplierDocument: supplier?.document,
        companyDocument: company?.document,
      })
    }

    setTimeout(() => {
      mixpanel.track(window.document.title, {
        companySupplierId: companySupplierId,
        current_path: pathname,
      })
    }, 2000)
  }

  useEffect(() => {
    if (window.document.title !== '') {
      mixpanelBrowser()
    }
    // eslint-disable-next-line
  }, [window.document.title])

  const loadDataWithAnimation = async (token, slug) => {
    setIsLoading(true)

    try {
      service.supplierPortal.auth.setPortalAccess({ slug, token })
      setPortalToken(token)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!response) return

    setCompany(response?.data?.company ?? null)
  }, [response])

  return (
    <SupplierJourneyContext.Provider
      value={{
        accessToken,
        activeStep,
        company,
        companySupplierToken,
        disableRecaptcha,
        handleAcceptedRecaptcha,
        handleNext,
        international,
        isLoading,
        linkInvalid,
        loadDataWithAnimation,
        loaded,
        nextDisabled,
        portalToken,
        recaptcha,
        recaptchaRef,
        refresh,
        resetRecaptcha,
        setAccessToken,
        setActiveStep,
        setCompanySupplierId,
        setCompanySupplierToken,
        setInternational,
        setNextDisabled,
        setRecaptcha,
        setStepInfo,
        setSteps,
        setSupplier,
        setUser,
        stepInfo,
        steps,
        supplier,
        user,
      }}
    >
      {children}
    </SupplierJourneyContext.Provider>
  )
}

export default SupplierJourneyProvider
